// Service-worker registration, update notification store and kill switch.
// Kept apart from React so main.jsx can call it before mounting.
import { registerSW } from 'virtual:pwa-register';

const updateStore = { available: false, listeners: new Set() };
let updateSWFn = null;

function setUpdateAvailable(v) {
  updateStore.available = v;
  updateStore.listeners.forEach((fn) => fn(v));
}
export function subscribeSwUpdate(fn) {
  updateStore.listeners.add(fn);
  fn(updateStore.available);
  return () => updateStore.listeners.delete(fn);
}
export function isSwUpdateAvailable() { return updateStore.available; }

export function registerOfflineSW() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  updateSWFn = registerSW({
    immediate: true,
    onNeedRefresh() { setUpdateAvailable(true); },
    onRegisterError(err) { console.warn('[offline] SW registration failed:', err?.message || err); },
  });
  return updateSWFn;
}

// Activates the waiting SW (SKIP_WAITING) and reloads.
export function applySwUpdate() {
  if (updateSWFn) updateSWFn(true);
  else window.location.reload();
}

// Browsers only check for a new SW on navigations. After an offline→online
// transition the page may have loaded from the precache while the server was
// unreachable, so ask for an update check explicitly.
export async function checkForSwUpdate() {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    await reg?.update();
  } catch { /* ignore */ }
}

export const SW_TENANT_MESSAGE = 'OD_SET_TENANT';

// Tell the SW which tenant this page belongs to so it can pick the
// per-tenant file/thumbnail caches. Re-sent on controller change.
export async function postTenantToSW(tenant, encrypted = false) {
  if (!tenant || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  const send = (worker) => { try { worker?.postMessage({ type: SW_TENANT_MESSAGE, tenant, encrypted: !!encrypted }); } catch { /* ignore */ } };
  try {
    const reg = await navigator.serviceWorker.ready;
    send(reg.active);
    send(reg.waiting);
    send(reg.installing);
  } catch { /* no registration */ }
  navigator.serviceWorker.addEventListener('controllerchange', () => send(navigator.serviceWorker.controller));
  send(navigator.serviceWorker.controller);
}

export const OFFLINE_CACHE_PREFIX = 'od-';

// Kill switch: unregister every SW on this origin and drop our caches.
// IndexedDB snapshots are left alone (harmless without the SW, and the
// switch is a safety valve, not "delete my data").
async function deleteOfflineCaches() {
  if (!('caches' in globalThis)) return;
  const keys = await caches.keys();
  await Promise.all(
    keys.filter((k) => k.startsWith(OFFLINE_CACHE_PREFIX) || k.startsWith('odx-') || k.startsWith('workbox-')).map((k) => caches.delete(k)),
  );
}

export async function applyKillSwitch() {
  if (typeof navigator === 'undefined') return;
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    await deleteOfflineCaches();
    // The SW that still controls THIS page may write a runtime cache while we
    // are cleaning up (e.g. /anlage.png). Sweep once more after it settles.
    setTimeout(() => { deleteOfflineCaches().catch(() => {}); }, 4000);
  } catch (err) {
    console.warn('[offline] kill switch cleanup failed:', err?.message || err);
  }
}

// Encrypted offline copy: the SW gets the CryptoKeys in memory only (never
// stored). After a restart it asks open pages with OD_NEED_KEY; this page
// answers whenever it holds a key, and pushes new keys (unlock, key fetch).
export const SW_KEYS_MESSAGE = 'OD_SET_KEYS';
let keyBridge = false;
export function bridgeKeysToSW(getKeys, onKeys) {
  if (keyBridge || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  keyBridge = true;
  const push = (target) => { const keys = getKeys(); if (keys) { try { target?.postMessage({ type: SW_KEYS_MESSAGE, keys }); } catch { /* ignore */ } } };
  navigator.serviceWorker.addEventListener('message', (e) => { if (e.data?.type === 'OD_NEED_KEY') push(e.source || navigator.serviceWorker.controller); });
  navigator.serviceWorker.addEventListener('controllerchange', () => push(navigator.serviceWorker.controller));
  onKeys(() => { navigator.serviceWorker.ready.then((reg) => push(reg.active)).catch(() => {}); push(navigator.serviceWorker.controller); });
  navigator.serviceWorker.ready.then((reg) => push(reg.active)).catch(() => {});
}

// Drop the keys the SW holds (boot of a locked/unlocking page, logout). A page
// that legitimately holds the key re-supplies it on the next OD_NEED_KEY.
export async function clearSWKeys() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  const send = (w) => { try { w?.postMessage({ type: 'OD_CLEAR_KEYS' }); } catch { /* ignore */ } };
  send(navigator.serviceWorker.controller);
  try { const reg = await navigator.serviceWorker.getRegistration(); send(reg?.active); } catch { /* ignore */ }
}
